/**
 * @author Tyrus Malmstrom.
 *
 * Video.js plugin that adds HLS / DASH logging to debug plalyer issues in real-time.
 */

/* eslint no-dupe-class-members: "off" */
/*
  eslint class-methods-use-this: ["off", {
    "exceptMethods": [
      "handleRemovingHlsDebugSessionStorageItems"
    ]
  }]
*/

import videojs from 'video.js';
import semver from 'semver';
import _ from 'lodash';

// Constants:
import { v4 as uuidv4 } from 'uuid';
import {
  CONSOLE_LOG_STYLE_TYPES,
} from '../../../snippets/js/constants.cjs';

const [Plugin, registerPlugin] = [
  videojs.getPlugin('plugin'),
  videojs.registerPlugin || videojs.plugin,
];

// ##################### Utilities #####################
function sortObject(obj) {
  if (typeof obj !== 'object') {
    return obj;
  }
  const temp = {};
  const keys = [];
  for (const key in obj) {
    keys.push(key);
  }
  keys.sort();
  for (const index in keys) {
    temp[keys[index]] = sortObject(obj[keys[index]]);
  }
  return temp;
}

const delay = (ms) => new Promise((res) => setTimeout(res, ms));

function timeRangesToString(r) {
  let log = '';
  for (let i = 0; i < r.length; i++) { // eslint-disable-line
    log += `[${r.start(i)}, ${r.end(i)}]`;
    log += ' ';
  }
  return log;
}
// ##################### End of Utilities #####################

class VhsLogger extends Plugin {
  #VhsPluginLogger = videojs.log.createLogger('VhsPluginLogger');

  #HlsJSLogger = videojs.log.createLogger('HlsJSLogger');

  #stats = null;

  #hlsDebugginVideo = document.getElementById('hls-debugging-video');

  constructor(player, options = {}) {
    super(player, options);
    if (semver.lt(videojs.VERSION, '7.18.1')) {
      throw new Error(
        `The VhsLogger plugin requires the video.js version 7.18.1. Please upgrade from your current version: ${videojs.VERSION}`,
      );
    }
    const {
      activeStream = {},
      enabled = false,
      uuid = uuidv4(),
      debugPrefix = 'VhsLoggerPluginPrefix',
      videoInstanceVm = {},
      vhsLoggerEnabled = false,
      hlsLoggerEnabled = false,
      vhsDebuggingOverload = false,
      hlsDebuggingOverload = false,
      thirdPartyCookiesEnabled = true,
    } = options;

    this.enabled = enabled;
    this.hlsDebuggingOverload = hlsDebuggingOverload;
    this.vhsDebuggingOverload = vhsDebuggingOverload;
    this.thirdPartyCookiesEnabled = thirdPartyCookiesEnabled;
    this.activeStream = activeStream;
    if (!this.#pluginShouldBeInitialized()) return;
    this.vhsLoggerEnabled = vhsLoggerEnabled;
    this.hlsLoggerEnabled = hlsLoggerEnabled;
    this.uuid = uuid;
    this.debugPrefix = debugPrefix || this.uuid;
    this.videoInstanceVm = videoInstanceVm;

    this.#log('VhsLogger', 'consturctor()');
    this.player.on('beforepluginsetup', () => {
      this.#log('VhsLogger', 'beforepluginsetup hook');
      this.#listenForVhsUsageEvents();
      if (this.vhsDebuggingOverload) {
        /* eslint no-console: "off" */
        console.log(
          '%cYou have enabled VHS debugging in overload mode... get ready for a lot of logs... 📖',
          CONSOLE_LOG_STYLE_TYPES.Success.join(';'),
        );
        videojs.log.level('debug');
      } else {
        videojs.log.level('info');
      }
      setTimeout(() => {
        this.#enablehlsDebuggingOverload();
      }, 1000);
    });
  }

  /**
   * Method that determines whether or not the plugin should be initialized.
   *
   * @returns {boolean} whether or not the plugin should be initialized
   */
   #pluginShouldBeInitialized() {
    if (!this.enabled) return this.#dispose();
    if (this.activeStream.type !== 'application/x-mpegURL') return this.#dispose();
    return true;
  }

  /**
   * Method to listen for vhs usage events.
   */
  #listenForVhsUsageEvents() {
     this.#log('listenForVhsUsageEvents()');
     this.player.tech().on('usage', (e) => {
       this.#log(
         'VhsLogger',
         '\nPlayer vhs usage event: ',
         {
           usageName: e.name,
           event: e,
         },
       );
     });
   }

  /**
   * Use Hls.js for further debugging.
   */
  async #enablehlsDebuggingOverload() {
    if (!this.hlsDebuggingOverload) return;
    /**
     * Make sure that the Hls.js video element is in sync with our current video.js video element:
     */
    this.player.on('play', () => {
      this.#hlsDebugginVideo.play();
    });
    this.player.on('pause', () => {
      this.#hlsDebugginVideo.pause();
    });
    this.player.on('seeked', () => {
      this.#hlsDebugginVideo.currentTime = this.player.currentTime();
    });
    console.log(
      '%cYou have enabled HLS debugging in overload mode... get ready for a lot of logs... 📖',
      CONSOLE_LOG_STYLE_TYPES.Success.join(';'),
    );
    this.#log('HlsJSLogger', 'enablehlsDebuggingOverload()');
    await delay(3000);
    const Hls = await import('hls.js');
    if (!Hls.isSupported()) return this.#log('Sorry... it looks like hls.js isn\'t supported on your browser!');
    this.#log('HlsJSLogger', `Hello from https://github.com/video-dev/hls.js/ using Version: ${Hls.default.version}`);
    this.hls = new Hls.default({ // eslint-disable-line
      debug: this.hlsDebuggingOverload,
    });
    this.hlsRuntimeEvents = Hls.default.Events;
    this.hlsErrorDetails = Hls.default.ErrorDetails;
    this.hlsErrorTypes = Hls.default.ErrorTypes;

    this.#log('HlsJSLogger', '\nLoading manifest and attaching video element...');
    this.hls.loadSource(this.activeStream.url);
    this.hls.attachMedia(this.#hlsDebugginVideo);

    // Setup hls.js events to listen on:
    this.#enableHlsJsEventListeners();
  }

  accessPlayerHistoryLogs() {
    if (this.#handlePluginDeregistered()) return;
    return this.player.log.history();
  }

  clearPlayerHistoryLogs() {
    if (this.#handlePluginDeregistered()) return;
    this.player.log.history.clear();
  }

  filterPlayerHistoryLogs(loggerName) {
    if (this.#handlePluginDeregistered()) return;
    if (_.isNil(loggerName)) {
      return console.log('%cYou have to pass in a specific logger name to filter on.', CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
    }
    return this.player.log.history.filter(loggerName);
  }

  /**
   * Method that allows the developer to access the current player instance VHS properties that are configured
   * at runtime.
   *
   * @param {String} [propertyType] Optional -- specific VHS runtime property to access
   *
   * @returns {Object} Runtime property to observe.
   */
  accessPlayerVhsRuntimeProperties(propertyType) {
    if (this.#handlePluginDeregistered()) return;
    const { vhs } = this.player.tech();
    if (!propertyType) {
      console.log('%c✅ Access Granted! Returning *all* player vhs runtime properties. ✅', CONSOLE_LOG_STYLE_TYPES.Success.join(';'));
      return vhs;
    }
    if (!_.get(vhs, propertyType, false)) {
      return console.log(`%c'${propertyType}' isn't a valid vhs runtime property.`, CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
    }
    console.log(`%c✅ Access Granted! Returning the '${propertyType}' player vhs runtime property. ✅`, CONSOLE_LOG_STYLE_TYPES.Success.join(';'));
    return _.get(vhs, propertyType);
  }

  /**
   * Hls.js specific events to listen to.
   */
  #HlsJSEventKeyListeners() {
    return {
      // ##################### Hls.JS Media Events ############################
      // fired before MediaSource is attaching to media element
      [this.hlsRuntimeEvents.MEDIA_ATTACHING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.MEDIA_ATTACHING',
          { event, data },
        );
      },
      // fired when MediaSource has been successfully attached to media element
      [this.hlsRuntimeEvents.MEDIA_ATTACHED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.MEDIA_ATTACHED',
          { event, data },
        );
      },
      // fired before detaching MediaSource from media element
      [this.hlsRuntimeEvents.MEDIA_DETACHING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.MEDIA_DETACHING',
          { event, data },
        );
        this.tracks = [];
      },
      // fired when MediaSource has been detached from media element
      [this.hlsRuntimeEvents.MEDIA_DETACHED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.MEDIA_DETACHED',
          { event, data },
        );
        clearInterval(this.hls.bufferTimer);
      },
      // ##################### End of Hls.JS Media Events ############################
      // ##################### Hls.JS Buffer Events ############################
      // fired when we buffer is going to be reset
      [this.hlsRuntimeEvents.BUFFER_RESET]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_RESET',
          { event, data },
        );
        clearInterval(this.hls.bufferTimer);
      },
      // fired when we know about the codecs that we need buffers for to push into
      [this.hlsRuntimeEvents.BUFFER_CODECS]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_CODECS',
          { event, data },
        );
      },
      // fired when sourcebuffers have been created
      [this.hlsRuntimeEvents.BUFFER_CREATED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_CREATED',
          { event, data },
        );
        this.tracks = data.tracks;
      },
      // fired when we append a segment to the buffer
      [this.hlsRuntimeEvents.BUFFER_APPENDING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_APPENDING',
          { event, data },
        );
      },
      // fired when we are done with appending a media segment to the buffer
      [this.hlsRuntimeEvents.BUFFER_APPENDED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_APPENDED',
          { event, data },
        );
      },
      // fired when the stream is finished and we want to notify the media buffer that there will be no more data
      [this.hlsRuntimeEvents.BUFFER_EOS]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_EOS',
          { event, data },
        );
      },
      // fired when the media buffer should be flushed
      [this.hlsRuntimeEvents.BUFFER_FLUSHING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_FLUSHING',
          { event, data },
        );
      },
      // fired when the media buffer has been flushed
      [this.hlsRuntimeEvents.BUFFER_FLUSHED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BUFFER_FLUSHED',
          { event, data },
        );
      },
      // ##################### End of Hls.JS Buffer Events ############################
      // ##################### Hls.JS Manifest Events ############################
      // fired to signal that a manifest loading starts
      [this.hlsRuntimeEvents.MANIFEST_LOADING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.MANIFEST_LOADING',
          { event, data },
          `\nmanifest loading: url ${data.url}`,
        );
      },
      // fired after manifest has been loaded
      [this.hlsRuntimeEvents.MANIFEST_LOADED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.MANIFEST_LOADED',
          { event, data },
          '\nmanifest loaded!',
        );
      },
      // fired after manifest has been parsed
      [this.hlsRuntimeEvents.MANIFEST_PARSED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.MANIFEST_PARSED',
          { event, data },
          `\nmanifest loaded, found ${data.levels.length} quality level${(data.levels.length > 1) ? 's' : ''}`,
        );
      },
      // ##################### End of Hls.JS Manifest Events ############################
      // ##################### Hls.JS Level Events ############################
      //  fired when a level switch is requested
      [this.hlsRuntimeEvents.LEVEL_SWITCHING]: (event, data) => { //  - fired when a level switch is requested
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LEVEL_SWITCHING',
          { event, data },
          '\nlevel switching requested...',
        );
      },
      // fired when a level switch is effective
      [this.hlsRuntimeEvents.LEVEL_SWITCHED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LEVEL_SWITCHED',
          { event, data },
          '\nlevel switched...',
        );
      },
      // fired when a level playlist loading starts
      [this.hlsRuntimeEvents.LEVEL_LOADING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LEVEL_LOADING',
          { event, data },
          '\nlevel playlist loading starts...',
        );
      },
      // fired when a level playlist loading finishes
      [this.hlsRuntimeEvents.LEVEL_LOADED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LEVEL_LOADED',
          { event, data },
          '\nlevel playlist loading finished...',
        );
        this.hls.isLive = data.details.live;
      },
      // fired when a level's details have been updated based on previous details, after it has been loaded
      [this.hlsRuntimeEvents.LEVEL_UPDATED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LEVEL_UPDATED',
          { event, data },
        );
      },
      //  fired when a level's PTS information has been updated after parsing a fragment
      [this.hlsRuntimeEvents.LEVEL_PTS_UPDATED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LEVEL_PTS_UPDATED',
          { event, data },
        );
      },
      // fired when a level is removed after calling `removeLevel()`
      [this.hlsRuntimeEvents.LEVELS_UPDATED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LEVELS_UPDATED',
          { event, data },
        );
      },
      // ##################### End of Hls.JS Level Events ############################
      // ##################### Hls.JS Audio Events ############################
      // fired to notify that audio track lists has been updated
      [this.hlsRuntimeEvents.AUDIO_TRACKS_UPDATED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.AUDIO_TRACKS_UPDATED',
          { event, data },
        );
      },
      // fired when an audio track switching is requested
      [this.hlsRuntimeEvents.AUDIO_TRACK_SWITCHING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.AUDIO_TRACK_SWITCHING',
          { event, data },
        );
      },
      // fired when an audio track switch actually occurs
      [this.hlsRuntimeEvents.AUDIO_TRACK_SWITCHED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.AUDIO_TRACK_SWITCHED',
          { event, data },
        );
      },
      // fired when an audio track loading starts
      [this.hlsRuntimeEvents.AUDIO_TRACK_LOADING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.AUDIO_TRACK_LOADING',
          { event, data },
        );
      },
      // fired when an audio track loading finishes
      [this.hlsRuntimeEvents.AUDIO_TRACK_LOADED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.AUDIO_TRACK_LOADED',
          { event, data },
        );
      },
      // ##################### End of Hls.JS Audio Events ############################
      // ##################### Hls.JS Subtitle Events ############################
      // fired to notify that subtitle track lists has been updated
      [this.hlsRuntimeEvents.SUBTITLE_TRACKS_UPDATED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.SUBTITLE_TRACKS_UPDATED',
          { event, data },
        );
      },
      // ??
      [this.hlsRuntimeEvents.SUBTITLE_TRACKS_CLEARED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.SUBTITLE_TRACKS_CLEARED',
          { event, data },
        );
      },
      // fired when a subtitle track loading starts
      [this.hlsRuntimeEvents.SUBTITLE_TRACK_LOADING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.SUBTITLE_TRACK_LOADING',
          { event, data },
        );
      },
      //  fired when a subtitle track loading finishes
      [this.hlsRuntimeEvents.SUBTITLE_TRACK_LOADED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.SUBTITLE_TRACK_LOADED',
          { event, data },
        );
      },
      // fired when a subtitle track switch occurs
      [this.hlsRuntimeEvents.SUBTITLE_TRACK_SWITCH]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.SUBTITLE_TRACK_SWITCH',
          { event, data },
        );
      },
      // fired when a subtitle fragment has been processed
      [this.hlsRuntimeEvents.SUBTITLE_FRAG_PROCESSED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.SUBTITLE_FRAG_PROCESSED',
          { event, data },
        );
      },
      // ##################### End of Hls.JS Subtitle Events ############################
      // ##################### Hls.JS Frag Events ############################
      // fired when the first timestamp is found
      [this.hlsRuntimeEvents.INIT_PTS_FOUND]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.INIT_PTS_FOUND',
          { event, data },
        );
      },
      // fired when a fragment loading starts
      [this.hlsRuntimeEvents.FRAG_LOADING]: (event, data) => { // - fired when a fragment loading starts
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_LOADING',
          { event, data },
          '\nFrag loading...',
        );
      },
      //  Identifier for fragment load aborting for emergency switch down
      [this.hlsRuntimeEvents.FRAG_LOAD_EMERGENCY_ABORTED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_LOAD_EMERGENCY_ABORTED',
          { event, data },
        );
      },
      // fired when a fragment loading is completed
      [this.hlsRuntimeEvents.FRAG_LOADED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_LOADED',
          { event, data },
        );
      },
      // fired when a fragment decryption is completed
      [this.hlsRuntimeEvents.FRAG_DECRYPTED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_DECRYPTED',
          { event, data },
        );
      },
      // fired when Init Segment has been extracted from fragment
      [this.hlsRuntimeEvents.FRAG_PARSING_INIT_SEGMENT]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_PARSING_INIT_SEGMENT',
          { event, data },
        );
      },
      // fired when parsing sei text is completed
      [this.hlsRuntimeEvents.FRAG_PARSING_USERDATA]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_PARSING_USERDATA',
          { event, data },
        );
      },
      // fired when parsing id3 is completed
      [this.hlsRuntimeEvents.FRAG_PARSING_METADATA]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_PARSING_METADATA',
          { event, data },
        );
      },
      // fired when fragment parsing is completed
      [this.hlsRuntimeEvents.FRAG_PARSED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_PARSED',
          { event, data },
        );
      },
      // fired when fragment remuxed MP4 boxes have all been appended into SourceBuffer
      [this.hlsRuntimeEvents.FRAG_BUFFERED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_BUFFERED',
          { event, data },
        );
        clearInterval(this.hls.bufferTimer);
        this.hls.bufferTimer = setInterval(this.checkBuffer.bind(this), 100);
      },
      // fired when fragment matching with current video position is changing
      [this.hlsRuntimeEvents.FRAG_CHANGED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FRAG_CHANGED',
          { event, data },
        );
      },
      // ##################### End of Hls.JS Frag Events ############################
      // ##################### Hls.JS FPS Events ############################
      // triggered when FPS drop in last monitoring period is higher than given threshold
      [this.hlsRuntimeEvents.FPS_DROP]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FPS_DROP',
          { event, data },
        );
      },
      //  triggered when FPS drop triggers auto level capping
      [this.hlsRuntimeEvents.FPS_DROP_LEVEL_CAPPING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.FPS_DROP_LEVEL_CAPPING',
          { event, data },
        );
      },
      // ##################### End of Hls.JS FPS Events ############################
      // ##################### Hls.JS Error Events ############################
      // Identifier for an error event
      [this.hlsRuntimeEvents.ERROR]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.ERROR',
          { event, data },
        );
        switch (data.details) {
          // raised when manifest loading fails because of a network error
          case this.hlsErrorDetails.MANIFEST_LOAD_ERROR:
            try {
              let manifestLoadErrorString = `Cannot load ${data.context.url}\nResponse code: ${data.response.code}\nResponse text: ${data.response.text}\n`;
              if (data.response.code === 0) {
                manifestLoadErrorString += 'This might be a CORS issue...';
              }
              this.#log(
                'HlsJSLogger',
                '\nHls.ErrorDetails.MANIFEST_LOAD_ERROR::try()',
                manifestLoadErrorString,
              );
            } catch (err) {
              this.#log(
                'HlsJSLogger',
                '\nHls.ErrorDetails.MANIFEST_LOAD_ERROR::catch()',
                `\nCannot load: ${data.context.url}.\nRespose body: ${data.response.text}.`,
              );
            }
            break;
          // raised when manifest loading fails because of a timeout
          case this.hlsErrorDetails.MANIFEST_LOAD_TIMEOUT:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.MANIFEST_LOAD_TIMEOUT',
              '\nTimeout while loading manifest...',
            );
            break;
          // raised when manifest parsing failed to find proper content
          case this.hlsErrorDetails.MANIFEST_PARSING_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.MANIFEST_PARSING_ERROR',
              `\nError while parsing manifest: ${data.reason}`,
            );
            break;
          // raised when loaded level contains no fragments
          case this.hlsErrorDetails.LEVEL_EMPTY_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.LEVEL_EMPTY_ERROR',
              `\nLoaded level contains no fragments: ${data.level} => ${data.url}`,
            );
            break;
          // raised when level loading fails because of a network error
          case this.hlsErrorDetails.LEVEL_LOAD_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.LEVEL_LOAD_ERROR',
              `\nError while loading level playlist: ${data.context.level} => ${data.url}`,
            );
            break;
          // raised when level loading fails because of a timeout
          case this.hlsErrorDetails.LEVEL_LOAD_TIMEOUT:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.LEVEL_LOAD_TIMEOUT',
              `\nTimeout while loading level playlist: ${data.context.level} => ${data.url}`,
            );
            break;
          // raised when level switching fails
          case this.hlsErrorDetails.LEVEL_SWITCH_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.LEVEL_SWITCH_ERROR',
              `\nError while trying to switch to level: ${data.level}`,
            );
            break;
          // raised when fragment loading fails because of a network error
          case this.hlsErrorDetails.FRAG_LOAD_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.FRAG_LOAD_ERROR',
              `\nError while loading fragment: ${data.frag.url}`,
            );
            break;
          // raised when fragment loading fails because of a timeout
          case this.hlsErrorDetails.FRAG_LOAD_TIMEOUT:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.FRAG_LOAD_TIMEOUT',
              `\nTimeout while loading fragment: ${data.frag.url}`,
            );
            break;
          // ??
          case this.hlsErrorDetails.FRAG_LOOP_LOADING_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.FRAG_LOOP_LOADING_ERROR',
              '\nFragment-loop loading error',
            );
            break;
          // raised when fragment decryption fails
          case this.hlsErrorDetails.FRAG_DECRYPT_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.FRAG_DECRYPT_ERROR',
              `\nDecrypting error: ${data.reason}`,
            );
            break;
          // raised when fragment parsing fails
          case this.hlsErrorDetails.FRAG_PARSING_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.FRAG_PARSING_ERROR',
              `\nParsing error: ${data.reason}`,
            );
            break;
          // raised when decrypt key loading fails because of a network error
          case this.hlsErrorDetails.KEY_LOAD_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.KEY_LOAD_ERROR',
              `\nError while loading key: ${data.frag.decryptdata.uri}`,
            );
            break;
          // raised when decrypt key loading fails because of a timeout
          case this.hlsErrorDetails.KEY_LOAD_TIMEOUT:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.KEY_LOAD_TIMEOUT',
              `\nTimeout while loading key: ${data.frag.decryptdata.uri}`,
            );
            break;
          // raised when exception is raised while calling buffer append
          case this.hlsErrorDetails.BUFFER_APPEND_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.BUFFER_APPEND_ERROR',
              '\nBuffer append error',
            );
            break;
          // raised when MediaSource fails to add new sourceBuffer
          case this.hlsErrorDetails.BUFFER_ADD_CODEC_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.BUFFER_ADD_CODEC_ERROR',
              `\nBuffer add codec error for => ${data.mimeType}:${data.error.message}`,
            );
            break;
          // raised when exception is raised during buffer appending
          case this.hlsErrorDetails.BUFFER_APPENDING_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.BUFFER_APPENDING_ERROR',
              '\nBuffer appending error',
            );
            break;
          // raised when playback is stuck because buffer is running out of data
          case this.hlsErrorDetails.BUFFER_STALLED_ERROR:
            this.#log(
              'HlsJSLogger',
              '\nHls.ErrorDetails.BUFFER_STALLED_ERROR',
              '\nBuffer stalled error',
            );
            break;
          default:
            break;
        }

        if (data.fatal) {
          this.#log(
            'HlsJSLogger',
            `\nHls.Error data fatal: ${data.fatal}`,
            `\nFatal error : ${data.details}`,
          );
          switch (data.type) {
            case this.hlsErrorTypes.MEDIA_ERROR:
              this.#log(
                'HlsJSLogger',
                '\nHls.ErrorTyes.MEDIA_ERROR',
                `\nA media error occurred: ${data.details}`,
              );
              break;
            case this.hlsErrorTypes.NETWORK_ERROR:
              this.#log(
                'HlsJSLogger',
                '\nHls.ErrorTyes.NETWORK_ERROR',
                `\nA network error occurred: ${data.details}`,
              );
              break;
            default:
              this.#log(
                'HlsJSLogger',
                `\nHls.ErrorTyes.${data.type}`,
                `\nAn unrecoverable error occurred: ${data.details}`,
              );
              this.hls.destroy();
              clearInterval(this.hls.bufferTimer);
              break;
          }
        }
        if (!this.#stats) {
          this.#stats = {};
        }

        // track all errors independently
        if (this.#stats[data.details] === undefined) {
          this.#stats[data.details] = 1;
        } else {
          this.#stats[data.details] += 1;
        }

        // track fatal error
        if (data.fatal) {
          if (this.#stats.fatalError === undefined) {
            this.#stats.fatalError = 1;
          } else {
            this.#stats.fatalError += 1;
          }
        }
        this.#log(
          'HlsJSLogger',
          '\nHls Error stats:',
          JSON.stringify(sortObject(this.#stats), null, '\t'),
        );
      },
      // ##################### End of Hls.JS Error Events ############################
      /**
       * fired when hls.js instance starts destroying. Different from MEDIA_DETACHED as one could want to detach
       * and reattach a video to the instance of hls.js to handle mid-rolls for example
       */
      [this.hlsRuntimeEvents.DESTROYING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.DESTROYING',
          { event, data },
        );
        clearInterval(this.hls.bufferTimer);
      },
      // fired when a decryption key loading starts
      [this.hlsRuntimeEvents.KEY_LOADING]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.KEY_LOADING',
          { event, data },
        );
      },
      // fired when a decryption key loading is completed
      [this.hlsRuntimeEvents.KEY_LOADED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.KEY_LOADED',
          { event, data },
        );
      },
      /**
       * When renderTextTracksNatively is false, this event will fire when a new captions or subtitle track is found,
       * in the place of adding a TextTrack to the video element.
       */
      [this.hlsRuntimeEvents.NON_NATIVE_TEXT_TRACKS_FOUND]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.NON_NATIVE_TEXT_TRACKS_FOUND',
          { event, data },
        );
      },
      // When renderTextTracksNatively is false, this event will fire when new captions or subtitle cues are parsed.
      [this.hlsRuntimeEvents.CUES_PARSED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.CUES_PARSED',
          { event, data },
        );
      },
      // ??
      [this.hlsRuntimeEvents.LIVE_BACK_BUFFER_REACHED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.LIVE_BACK_BUFFER_REACHED',
          { event, data },
        );
      },
      // ??
      [this.hlsRuntimeEvents.BACK_BUFFER_REACHED]: (event, data) => {
        this.#log(
          'HlsJSLogger',
          '\nHls.default.Events.BACK_BUFFER_REACHED',
          { event, data },
        );
      },
    };
  }

  #enableHlsJsEventListeners() {
    this.#disableHlsEventListeners();
    if (!_.isObject(this.hlsRuntimeEvents) || !this.player) return;
    for (const key of Object.keys(this.hlsRuntimeEvents)) {
      const boundFn = this.#HlsJSEventKeyListeners.bind(this);
      this.hls.on(this.hlsRuntimeEvents[key], boundFn()[this.hlsRuntimeEvents[key]]);
    }
  }

  #disableHlsEventListeners() {
    if (!_.isObject(this.hlsRuntimeEvents) || !this.player) return;
    for (const key of Object.keys(this.hlsRuntimeEvents)) {
      const boundFn = this.#HlsJSEventKeyListeners.bind(this);
      this.hls.off(this.hlsRuntimeEvents[key], boundFn()[this.hlsRuntimeEvents[key]]);
    }
  }

  /**
   * check buffer state of current HLS stream.
   */
  checkBuffer() {
    let log = `Duration: ${this.#hlsDebugginVideo.duration}\nBuffered: ${timeRangesToString(
      this.#hlsDebugginVideo.buffered,
    )}\nSeekable: ${timeRangesToString(
      this.#hlsDebugginVideo.seekable,
    )}\nPlayed: ${timeRangesToString(this.#hlsDebugginVideo.played)}\n`;
    if (this.hls.media) {
      for (const type in this.tracks) {
        log += `Buffer for ${type} contains:${timeRangesToString(
          this.tracks[type].buffer.buffered,
        )}\n`;
      }
      const videoPlaybackQuality = this.#hlsDebugginVideo.getVideoPlaybackQuality;
      if (
        videoPlaybackQuality
        && typeof videoPlaybackQuality === typeof Function
      ) {
        log += `Dropped frames: ${
          this.#hlsDebugginVideo.getVideoPlaybackQuality().droppedVideoFrames
        }\n`;
        log += `Corrupted frames: ${
          this.#hlsDebugginVideo.getVideoPlaybackQuality().corruptedVideoFrames
        }\n`;
      } else if (this.#hlsDebugginVideo.webkitDroppedFrameCount) {
        log += `Dropped frames: ${this.#hlsDebugginVideo.webkitDroppedFrameCount}\n`;
      }
    }
    log += `Bandwidth Estimate: ${this.hls.bandwidthEstimate.toFixed(3)}\n`;
    if (this.hls.isLive) {
      log
        += 'Live Stats:\n'
        + `  Max Latency: ${this.hls.maxLatency}\n`
        + `  Target Latency: ${this.hls.targetLatency.toFixed(3)}\n`
        + `  Latency: ${this.hls.latency.toFixed(3)}\n`
        + `  Drift: ${this.hls.drift.toFixed(3)} (edge advance rate)\n`
        + `  Edge Stall: ${this.hls.latencyController.edgeStalled.toFixed(
          3,
        )} (playlist refresh over target duration/part)\n`
        + `  Playback rate: ${this.#hlsDebugginVideo.playbackRate.toFixed(2)}\n`;
    }
    // append log to element:
    document.getElementById('bufferedOut').textContent = log;
  }

  /**
   * A function that logs a color-coded message to the console if `debug` is `true`.
   *
   * @param {String} logger The specified logger type to use
   * @param {Object} args - A destructred array of all arguments passed into the `log` function which are then logged out in that same order.
   *
   */
   #log(logger = 'VhsLogger', ...args) {
    if (this.enabled) {
      if (logger === 'VhsLogger' && this.vhsLoggerEnabled) this.#VhsPluginLogger(`(${this.debugPrefix}) =>`, ...args);
      if (logger === 'HlsJSLogger' && this.hlsLoggerEnabled) this.#HlsJSLogger(`(${this.debugPrefix}) =>`, ...args);
    }
  }

  #handlePluginDeregistered() {
     if (this.player === null) {
       console.log('%c🚫 Access Denied 🚫 -- This is because the \'VhsLogger\' plugin has been disposed...🤦', CONSOLE_LOG_STYLE_TYPES.Error.join(';'));
       return true;
     }
     return false;
   }

  #handleRemovingHlsDebugSessionStorageItems() {
    if (window.sessionStorage.getItem('vhsLoggerEnabled')) window.sessionStorage.removeItem('vhsLoggerEnabled');
    if (window.sessionStorage.getItem('hlsLoggerEnabled')) window.sessionStorage.removeItem('hlsLoggerEnabled');
    if (window.sessionStorage.getItem('hlsDebuggingOverload')) window.sessionStorage.removeItem('hlsDebuggingOverload');
    if (window.sessionStorage.getItem('showHlsDebugginVideoOverlay')) window.sessionStorage.removeItem('showHlsDebugginVideoOverlay');
    if (window.sessionStorage.getItem('showHlsDebugginStatsContainer')) window.sessionStorage.removeItem('showHlsDebugginStatsContainer');
  }

  #dispose() {
    super.dispose();
    if (this?.thirdPartyCookiesEnabled) {
      this.#handleRemovingHlsDebugSessionStorageItems();
    }
    if (this.hlsDebuggingOverload) this.#disableHlsEventListeners();
    videojs.log('The VhsLogger plugin has been disposed.');
  }
}

// Include the version number.
VhsLogger.VERSION = '1.0.0';

// (╯°□°)╯︵ ┻━┻ ┻━┻︵ \(°□°)/ ︵ ┻━┻ \\

registerPlugin('VhsLogger', VhsLogger);
export default VhsLogger;
